import { useContext, useEffect, useState } from "react";
import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import moment from "moment";

import MUIStack from "@mui/material/Stack";
import MUIBox from "@mui/material/Box";
import MUIButton from "@mui/material/Button";
import MUIAddIcon from "@mui/icons-material/Add";
import MUISaveIcon from "@mui/icons-material/Save";
import MUITable from "@mui/material/Table";
import MUIGrid from "@mui/material/Grid2";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUIFormControlLabel from "@mui/material/FormControlLabel";
import MUIFormControl from "@mui/material/FormControl";
import MUIRadioGroup from "@mui/material/RadioGroup";
import MUIRadio from "@mui/material/Radio";
import MUITableBody from "@mui/material/TableBody";
import MUITableRow from "@mui/material/TableRow";
import MUITableCell from "@mui/material/TableCell";
import MUITextField from "@mui/material/TextField";
import MUITypography from "@mui/material/Typography";
import MUIFileDownloadIcon from "@mui/icons-material/FileDownload";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import MUISearchIcon from "@mui/icons-material/Search";
import MUILabelIcon from "@mui/icons-material/Label";
import MUIInputAdornment from "@mui/material/InputAdornment";
import { yellow } from "@mui/material/colors";
import { grey } from "@mui/material/colors";
import MUIIconButton from "@mui/material/IconButton";
import MUIEditIcon from "@mui/icons-material/Edit";
import MUICancelIcon from "@mui/icons-material/Cancel";
import MUICheckIcon from "@mui/icons-material/Check";
import MUITextsmsIcon from "@mui/icons-material/Textsms";
import MUIEventIcon from "@mui/icons-material/Event";
import MUICheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import MUICheckBoxIcon from "@mui/icons-material/CheckBox";
import * as MUIColors from "@mui/material/colors";
import MUIAssignmentIcon from "@mui/icons-material/Assignment";
import MUISubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import MUIFilterListIcon from "@mui/icons-material/FilterList";
import MUIRocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import MUIBadge from "@mui/material/Badge";
import MUIAlert from "@mui/material/Alert";

import Menu from "../../Menu";
import Card from "../../Card";
import EmptyState from "../../EmptyState";
import MainContext from "../../../Context/MainContext/MainContext";
import User from "../../../Utility/Crud/User";
import Exporter from "../../../Utility/Exporter";
import Patient from "../../../Utility/Crud/Patient";
import OrderStatusReason from "../../../Utility/Crud/OrderStatusReason";
import Term from "../../../Utility/Crud/Term";
import Setting from "../../../Utility/Setting";
import Calendar from "../../../Utility/Calendar";
import Note from "../../../Utility/Crud/Note";

/**
 * CardChartNotes renders a Card component displaying patient demographic
 * information.
 * @returns {JSX.Element}
 */

const title = "Notes";
const icon = MUILabelIcon;
const className = "CardChartNotes";

export default function CardChartNotes({}) {
  const [createNote, setCreateNote] = useState(false);
  const defaultNoteType = "note";
  const followupNoteType = "followup";
  const taskNoteType = "task";
  const [noteType, setNoteType] = useState(defaultNoteType);
  const [noteText, setNoteText] = useState("");
  const [sensitiveDataFindings, setSensitiveDataFindings] = useState([]);
  const [sensitiveDataBypass, setSensitiveDataBypass] = useState(false);
  const [pendingFollowUpSave, setPendingFollowUpSave] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [expandedNotes, setExpandedNotes] = useState({});
  const [noteTypeVisibility, setNoteTypeVisibility] = useState(
    Setting.get("chart.notes.visibility") || {},
  );
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState("");
  const [pendingNoteUpdate, setPendingNoteUpdate] = useState(false);
  const [pendingTaskComplete, setPendingTaskComplete] = useState(false);
  const chartContext = useContext(ChartContext);
  const mainContext = useContext(MainContext);
  const [noteAssignedToId, setNoteAssignedToId] = useState(
    mainContext.currentUser?.id || null,
  );
  const NOTE_PREVIEW_LENGTH = 300;

  /**
   * Returns the default number of follow-up days for an order. For UHC
   * Medicare Replacement (insuranceSubTypeId 196) in Preauth Followup -
   * Appeals (orderStatusReasonId 89), and if less than 15 days have passed
   * since lastOrderStatusReasonIdChange, returns 20. Otherwise, returns 5.
   * @returns {number} Default follow-up days
   */
  const getDefaultFollowupDays = () => {
    if (
      (chartContext.orderId &&
        chartContext.chart.orderActivity[chartContext.orderId]
          ?.insuranceSubTypeId === 196 && // UHC MCR Replacement
        chartContext.chart.orderActivity[chartContext.orderId]
          ?.orderStatusReasonId === 89 && // Preauth Followup - Appeals
        moment().diff(
          moment(
            chartContext.chart.orderActivity[chartContext.orderId]
              ?.lastOrderStatusReasonIdChange,
          ),
          "days",
        ) < 15) ||
      (chartContext.orderId &&
        chartContext.chart.orderActivity[chartContext.orderId]
          ?.orderStatusReasonId === 110 && // UHC Denied Appeals - Maximus
        moment().diff(
          moment(
            chartContext.chart.orderActivity[chartContext.orderId]
              ?.lastOrderStatusReasonIdChange,
          ),
          "days",
        ) < 15)
    ) {
      return 20;
    } else if (
      chartContext.orderId &&
      chartContext.chart.orderActivity[chartContext.orderId]
        ?.orderStatusReasonId === 57 // Service - Documentation Follow Up
    ) {
      return 3;
    }

    return 5;
  };

  const getMaxFollowupDays = () => {
    return 30;
  };

  /**
   * Returns whether to use business days for follow-up date calculation. Uses
   * same logic as getDefaultFollowupDays: if UHC MCR Replacement Preauth
   * Followup - Appeals, returns false; else true.
   * @returns {boolean}
   */
  const getDefaultUseBusinessDays = () => {
    if (
      chartContext.chart.orderActivity[chartContext.orderId]
        ?.insuranceSubTypeId === 196 &&
      chartContext.chart.orderActivity[chartContext.orderId]
        ?.orderStatusReasonId === 89 &&
      moment().diff(
        moment(
          chartContext.chart.orderActivity[chartContext.orderId]
            ?.lastOrderStatusReasonIdChange,
        ),
        "days",
      ) < 15
    ) {
      return false;
    }
    return true;
  };

  const [followUpDays, setFollowUpDays] = useState(getDefaultFollowupDays());

  /**
   * Renders the audit log order status reason as a labeled icon and text.
   * @param {Object} orderStatusReason - The order status reason object.
   * @returns {JSX.Element}
   */
  const renderAuditLogOrderStatusReasonId = (orderStatusReason) => {
    const orderStatus =
      mainContext.orderStatusesIndexed[orderStatusReason.orderStatus];
    return (
      <MUIStack direction="row" spacing={1} sx={{ pt: 1.5 }}>
        <MUILabelIcon
          fontSize="small"
          sx={{
            color: orderStatus
              ? MUIColors[mainContext.colorsIndexed[orderStatus.colorId].hue][
                  mainContext.colorsIndexed[orderStatus.colorId].shade
                ]
              : grey[500],
          }}
        />
        <MUITypography variant="body2">
          {OrderStatusReason.getDisplayName(orderStatusReason)}
        </MUITypography>
      </MUIStack>
    );
  };

  useEffect(() => {
    if (pendingFollowUpSave) {
      chartContext.handleSaveChart();
      setPendingFollowUpSave(false);
      setCreateNote(false);
      setNoteText("");
      setFollowUpDays(getDefaultFollowupDays());
      setNoteAssignedToId(mainContext.currentUser.id);
      setNoteType(defaultNoteType);
      setSensitiveDataFindings([]);
      setSensitiveDataBypass(false);
    }
  }, [pendingFollowUpSave]);

  /**
   * Handles clicking the checkmark to update a note.
   */
  const handleUpdateNote = () => {
    const originalNote = chartContext.originalChart.note[editingNoteId];
    const originalCreatedAt = originalNote.createdAt;
    let editingNoteTextWithEditedBy = editingNoteText;

    const now = moment();
    const createdMoment = moment.utc(originalCreatedAt);
    // Only append if more than 1 minute has passed since creation
    if (now.diff(createdMoment, "minutes") > 1) {
      const date = new Date();
      const timeZoneString = date.toLocaleTimeString("en-us", {
        timeZoneName: "short",
      });
      const timeZoneAbbreviation = timeZoneString.split(" ").pop() || "Local";
      editingNoteTextWithEditedBy += `\n\n[Edited by ${User.getDisplayName(mainContext.currentUser)} on ${now.local().format("M/D/YYYY h:mma")} ${timeZoneAbbreviation}]`;
    }

    chartContext.onChange({
      note: {
        [editingNoteId]: {
          text: editingNoteTextWithEditedBy,
        },
      },
    });
    setPendingNoteUpdate(true);
  };

  /**
   * Handles clicking the checkmark to complete a task.
   * Updates the note with completed=true and appends a completion flag.
   */
  const handleCompleteTask = (note) => {
    const now = moment();
    const date = new Date();
    const timeZoneString = date.toLocaleTimeString("en-us", {
      timeZoneName: "short",
    });
    const timeZoneAbbreviation = timeZoneString.split(" ").pop() || "Local";
    const completedBlurb = `[Completed by ${User.getDisplayName(mainContext.currentUser)} on ${now.local().format("M/D/YYYY h:mma")} ${timeZoneAbbreviation}]`;
    const completedText = `${note.text}\n\n${completedBlurb}`;

    chartContext.onChange({
      note: {
        [note.id]: {
          text: completedText,
          completed: true,
        },
      },
    });
    setPendingTaskComplete(true);
  };

  useEffect(() => {
    if (pendingNoteUpdate) {
      chartContext.handleSaveChart(null, async () => {
        setPendingNoteUpdate(false);
        setEditingNoteId(null);
        setEditingNoteText("");
      });
    }
  }, [pendingNoteUpdate]);

  useEffect(() => {
    if (pendingTaskComplete) {
      chartContext.handleSaveChart(null, async () => {
        setPendingTaskComplete(false);
      });
    }
  }, [pendingTaskComplete]);

  // Filter notes by note text or user and type visibility
  const trimmedSearchValue = searchValue.trim().toLowerCase();

  /**
   * Highlights matching search text in a string using <mark> and MUI yellow
   * color.
   * @param {string} text - The text to highlight matches in.
   * @returns {Array|String} Highlighted JSX or original text.
   */
  const highlightMatch = (text) => {
    if (!trimmedSearchValue) {
      return text;
    }
    const regex = new RegExp(
      `(${trimmedSearchValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi",
    );
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? (
        <mark key={i} style={{ backgroundColor: yellow[600], padding: 0 }}>
          {part}
        </mark>
      ) : (
        part
      ),
    );
  };

  /**
   * Renders a note, handling edit mode, truncation, and highlighting.
   * @param {Object} note - The note object.
   * @returns {JSX.Element}
   */
  const renderNote = (note) => {
    // Handles both patient and order notes.
    const noteContent = note.text ?? note.note ?? "";

    // Editing mode
    if (editingNoteId === note.id) {
      return (
        <MUITextField
          multiline
          fullWidth
          size="small"
          value={editingNoteText}
          onChange={(e) => setEditingNoteText(e.target.value)}
          variant="standard"
          sx={{ "& .MuiInputBase-input": { typography: "body2" } }}
        />
      );
    }
    const isLong = noteContent.length > NOTE_PREVIEW_LENGTH;
    const isExpanded = expandedNotes[note.id];
    const displayText =
      isExpanded || !isLong
        ? noteContent
        : noteContent.slice(0, NOTE_PREVIEW_LENGTH) + "...";

    // Match all edit/complete flags at the end of the note
    const editFlagPattern =
      /(\n*\[Edited by.*?\]$|\n*\[Completed by.*?\]$|--> Completed by.*?$|\n*-- Updated:.*?-- By:.*?$|\n*-- Completed:.*?-- By:.*?$)/i;
    let mainText = displayText;
    let editFlags = [];

    // Find all flags at the end (allow multiple)
    let flagMatch = true;
    while (flagMatch) {
      flagMatch = mainText.match(editFlagPattern);
      if (flagMatch) {
        const idx = flagMatch.index;
        editFlags.unshift(flagMatch[0].trim());
        mainText = mainText.slice(0, idx).replace(/\n+$/, "");
      }
    }

    // Structure with icon for tasks
    return (
      <MUIStack
        direction="row"
        spacing={1}
        sx={{
          alignItems: "flex-start",
          pt: 1.5,
        }}
      >
        {note.task && (
          <MUIAssignmentIcon
            fontSize="small"
            sx={{ color: (theme) => theme.palette.primary.main, mt: "2px" }}
          />
        )}
        <MUIBox sx={{ flex: 1 }}>
          <MUITypography variant="body2">
            {highlightMatch(mainText)}
          </MUITypography>
          {editFlags.length > 0 &&
            editFlags.map((flag, idx) => (
              <MUITypography
                key={idx}
                variant="caption"
                sx={{
                  color: (theme) => theme.palette.text.disabled,
                  display: "block",
                }}
              >
                {flag}
              </MUITypography>
            ))}
          {isLong && (
            <MUIBox>
              <MUIButton
                size="small"
                sx={{ mt: 1, p: 0, minWidth: "unset" }}
                onClick={() =>
                  setExpandedNotes((prev) => ({
                    ...prev,
                    [note.id]: !isExpanded,
                  }))
                }
              >
                {isExpanded ? "Show Less" : "Show More"}
              </MUIButton>
            </MUIBox>
          )}
        </MUIBox>
      </MUIStack>
    );
  };

  /**
   * Renders a notification note with icon and text.
   * @param {string} text - The notification text.
   * @returns {JSX.Element}
   */
  const renderNotification = (text) => (
    <MUIStack direction="row" spacing={1} sx={{ pt: 1.5 }}>
      <MUITextsmsIcon
        fontSize="small"
        sx={{ color: (theme) => theme.palette.primary.main }}
      />
      <MUITypography variant="body2">{text}</MUITypography>
    </MUIStack>
  );

  /**
   * Renders a followup date note with icon and text.
   * @param {string} text - The followup date text.
   * @returns {JSX.Element}
   */
  const renderAuditLogFollowupDate = (text) => (
    <MUIStack direction="row" spacing={1} sx={{ pt: 1.5 }}>
      <MUIEventIcon
        fontSize="small"
        sx={{ color: (theme) => theme.palette.primary.main }}
      />
      <MUITypography variant="body2">{text}</MUITypography>
    </MUIStack>
  );

  const renderOrderCreated = (text) => (
    <MUIStack direction="row" spacing={1} sx={{ pt: 1.5 }}>
      <MUIRocketLaunchIcon
        fontSize="small"
        sx={{ color: (theme) => theme.palette.success.main }}
      />
      <MUITypography variant="body2">{text}</MUITypography>
    </MUIStack>
  );

  // Unify the multiple different note types into a single one to make
  // rendering easier.
  let notes = [];
  if (chartContext.orderId) {
    Object.values(chartContext.originalChart.note)
      .filter(
        (note) =>
          note.activityId == chartContext.orderId && note.noteTypeTermId !== 88,
      )
      .forEach((note) => {
        notes.push({
          id: note.id,
          // Separate tasks and notes by type
          noteTypeTermId: note.noteTypeTermId,
          type: note.task ? "task" : "note",
          text: note.text ?? "",
          user:
            User.getDisplayName(mainContext.usersIndexed[note.createdById]) ||
            "Unknown",
          timestamp: moment.utc(note.createdAt),
          createdById: note.createdById,
          // Lots of notes have this set even though it's really only useful for tasks.
          assignedToId: note.task ? note.assignedToId : null,
          completed: note.completed,
          createdAt: note.createdAt,
          render: renderNote(note),
        });
      });

    // Add audit log records as notes
    Object.values(chartContext.originalChart.auditLog)
      .filter(
        (auditLog) =>
          auditLog.tableName === "order_activity" &&
          auditLog.rowId == chartContext.orderId &&
          auditLog.newValue &&
          (auditLog.columnName === "order_status_reason_id" ||
            auditLog.columnName === "follow_up_date"),
      )
      .forEach((auditLog) => {
        if (auditLog.columnName === "order_status_reason_id") {
          let orderStatusReason =
            auditLog.newValue &&
            chartContext.chart.orderStatusReason[auditLog.newValue];
          notes.push({
            type: "auditLog.orderStatusReasonId",
            text: orderStatusReason
              ? OrderStatusReason.getDisplayName(orderStatusReason)
              : "Routed to unknown reason",
            user:
              User.getDisplayName(
                mainContext.usersIndexed[auditLog.userProfileId],
              ) || "Unknown",
            timestamp: moment.utc(auditLog.timestamp),
            render: orderStatusReason
              ? renderAuditLogOrderStatusReasonId(orderStatusReason)
              : null,
          });
        } else if (auditLog.columnName === "follow_up_date") {
          // Format the date for display
          const dateText = auditLog.newValue
            ? moment.utc(auditLog.newValue).local().format("M/D/YYYY")
            : "Unknown date";
          const followupText = `Followup date set to ${dateText}`;
          notes.push({
            type: "auditLog.followupDate",
            text: followupText,
            user:
              User.getDisplayName(
                mainContext.usersIndexed[auditLog.userProfileId],
              ) || "Unknown",
            timestamp: moment.utc(auditLog.timestamp),
            render: renderAuditLogFollowupDate(followupText),
          });
        }
      });

    // Add delivered notifications as notes
    Object.values(chartContext.chart.notification)
      .filter(
        (notification) =>
          notification.orderActivityId == chartContext.orderId &&
          notification.notificationStatusTermId === 103, // Delivered
      )
      .forEach((notification) => {
        const notificationText = `Delivered ${Term.getDisplayName(mainContext.termsIndexed[notification.notificationChannelTermId])} for ${Term.getDisplayName(mainContext.termsIndexed[notification.notificationTypeTermId])}`;
        notes.push({
          type: "notification",
          text: notificationText,
          user: "System",
          timestamp: moment.utc(
            notification.lastNotificationStatusTermIdChange,
          ),
          render: renderNotification(notificationText),
        });
      });

    // Add "Created" note from activity startedAt
    const orderActivity =
      chartContext.chart.orderActivity[chartContext.orderId];
    const activity = chartContext.chart.activity[chartContext.orderId];
    if (activity && activity.startedAt) {
      const orderCreatedText = `Created ${orderActivity.orderType} ${activity?.type}`;
      notes.push({
        type: "auditLog.orderStatusReasonId",
        text: orderCreatedText,
        user: "System",
        timestamp: moment.utc(activity.startedAt),
        render: renderOrderCreated(orderCreatedText),
      });
    }
  } else {
    Object.values(chartContext.originalChart.patientNote).forEach((note) => {
      notes.push({
        type: "patientNote",
        noteTypeTermId: note.noteTypeTermId,
        text: note.note ?? "",
        user: note.noteAddedBy || "Unknown",
        timestamp: moment.utc(note.noteAddedOn),
        render: renderNote(note),
      });
    });
  }

  // Sort notes by most recent first
  notes.sort((a, b) => b.timestamp.diff(a.timestamp));

  const filteredNotes = notes.filter((note) => {
    const visible =
      noteTypeVisibility[note.type] !== undefined
        ? noteTypeVisibility[note.type]
        : true;
    return (
      visible &&
      (note.text.trim().toLowerCase().includes(trimmedSearchValue) ||
        note.user.trim().toLowerCase().includes(trimmedSearchValue))
    );
  });

  /**
   * Renders the add note field for creating a new note.
   * Handles note type selection and assignment.
   * @returns {JSX.Element}
   */
  const renderAddNoteField = () => {
    const handleNoteTextChange = (value) => {
      setNoteText(value);

      const findings = Note.mayContainSensitiveData({ text: value });
      setSensitiveDataFindings(findings);

      // Require a fresh bypass confirmation only after sensitive data is removed.
      if (findings.length === 0) {
        setSensitiveDataBypass(false);
      }
    };

    return (
      <MUIBox sx={{ mb: 3 }}>
        <MUITextField
          multiline
          rows={4}
          fullWidth={true}
          label={"Note Text"}
          value={noteText}
          onChange={(e) => handleNoteTextChange(e.target.value)}
          variant="standard"
          sx={{ mb: 2 }}
          slotProps={{
            htmlInput: { style: { resize: "vertical" } },
            inputLabel: { shrink: true },
          }}
        />
        {sensitiveDataFindings.length > 0 ? (
          <MUIAlert severity="warning" sx={{ mb: 2 }}>
            This note may contain sensitive data (
            {sensitiveDataFindings.join(", ")}). Remove it, or confirm below to
            continue.
            <MUIBox sx={{ mt: 1 }}>
              <MUIButton
                size="small"
                fullWidth
                variant={sensitiveDataBypass ? "outlined" : "contained"}
                color="warning"
                sx={{ textAlign: "center" }}
                onClick={() => setSensitiveDataBypass(true)}
              >
                I confirm there is no sensitive data in this note
              </MUIButton>
            </MUIBox>
          </MUIAlert>
        ) : null}
        {chartContext.orderId && (
          <MUIFormControl>
            <MUIRadioGroup
              row
              defaultValue={defaultNoteType}
              value={noteType}
              onChange={(e) => setNoteType(e.target.value)}
            >
              <MUIFormControlLabel
                value={defaultNoteType}
                control={<MUIRadio size="small" />}
                label="Note"
              />
              <MUIFormControlLabel
                value={taskNoteType}
                control={<MUIRadio size="small" />}
                label="Task"
              />
              <MUIFormControlLabel
                value={followupNoteType}
                control={<MUIRadio size="small" />}
                label="Followup"
              />
            </MUIRadioGroup>
          </MUIFormControl>
        )}
        {noteType === taskNoteType && (
          <MUIAutocomplete
            options={User.getActiveUsers(mainContext.usersIndexed)}
            value={
              noteAssignedToId
                ? mainContext.usersIndexed[noteAssignedToId]
                : null
            }
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            getOptionLabel={(option) => User.getDisplayName(option) || ""}
            onChange={(e, newValue) => {
              setNoteAssignedToId(
                newValue ? newValue.id : mainContext.currentUser.id,
              );
            }}
            renderInput={(params) => (
              <MUITextField
                {...params}
                size="small"
                variant="standard"
                label="Assign To"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}
            disableClearable
          />
        )}
        {noteType === followupNoteType && (
          <MUIGrid container spacing={1}>
            <MUIGrid sx={{ width: "100px" }}>
              <MUITypography variant="body1">Follow up in</MUITypography>
            </MUIGrid>
            <MUIGrid sx={{ width: "50px" }}>
              <MUITextField
                type="number"
                size="small"
                variant="standard"
                value={followUpDays}
                onChange={(e) => {
                  if (
                    e.target.value > getMaxFollowupDays() ||
                    e.target.value < 0
                  ) {
                    return;
                  }
                  setFollowUpDays(Number(e.target.value));
                }}
                fullWidth
                slotProps={{
                  inputLabel: { shrink: true },
                }}
              />
            </MUIGrid>
            <MUIGrid>
              <MUITypography variant="body1">
                {getDefaultUseBusinessDays()
                  ? "business days"
                  : "calendar days"}{" "}
                (
                {moment(
                  Calendar.addDays({
                    daysToAdd: followUpDays,
                    useBusinessDays: getDefaultUseBusinessDays(),
                  }),
                ).format("ddd, MMM D")}
                )
              </MUITypography>
            </MUIGrid>
          </MUIGrid>
        )}
      </MUIBox>
    );
  };

  /**
   * Determines which view to render based on edit mode state. Renders search
   * box, add note field, notes table, or empty state.
   * @returns {JSX.Element} Either edit view or read-only view.
   */
  const renderContent = () => {
    return (
      <>
        <MUIBox sx={{ position: "sticky", top: 0, zIndex: 1 }}>
          <MUITextField
            placeholder="Type to search..."
            hiddenLabel={true}
            fullWidth={true}
            variant="filled"
            size="small"
            onChange={(e) => setSearchValue(e.target.value)}
            sx={{ mb: 2 }}
            slotProps={{
              input: {
                startAdornment: (
                  <MUIInputAdornment position="start">
                    <MUISearchIcon />
                  </MUIInputAdornment>
                ),
                disableUnderline: true,
                style: { borderRadius: 4 },
              },
            }}
          />
          {createNote && renderAddNoteField()}
        </MUIBox>
        {filteredNotes.length === 0 ? (
          <MUIBox>
            <EmptyState line1="No Notes" line2="Add a note to get started" />
          </MUIBox>
        ) : (
          <MUIBox
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
              maxHeight: "calc(100vh - 300px)",
            }}
          >
            <MUITable stickyHeader>
              <MUITableBody>
                {filteredNotes.map((note, i) => {
                  // Only allow editing for "note" and "task" type, not "patientNote"
                  let canEdit = false;
                  if (
                    note.type === "note" ||
                    (note.type === "task" && note.id)
                  ) {
                    canEdit =
                      note.createdById === mainContext.currentUser.id &&
                      note.createdAt &&
                      moment().diff(moment.utc(note.createdAt), "hours") < 24;
                  }

                  // Get assigned user if present
                  const assignedUser = note.assignedToId
                    ? mainContext.usersIndexed[note.assignedToId]
                    : null;

                  // Flag for completing tasks
                  const canComplete =
                    note.type === "task" &&
                    note.assignedToId === mainContext.currentUser.id &&
                    !note.completed;
                  return (
                    <MUITableRow
                      key={note.id || i}
                      sx={{
                        backgroundColor:
                          i % 2 === 0
                            ? (theme) => theme.palette.action.hover
                            : "inherit",
                      }}
                    >
                      <MUITableCell
                        style={{
                          width: "150px",
                          whiteSpace: "nowrap",
                          verticalAlign: "top",
                          padding: "2px 0px 2px 6px",
                        }}
                      >
                        <MUITypography variant="subtitle2">
                          {highlightMatch(
                            moment(note.timestamp)
                              .local()
                              .format("M/D/YYYY h:mma"),
                          )}
                        </MUITypography>
                        <MUITypography
                          variant="subtitle2"
                          sx={{
                            color: "text.secondary",
                          }}
                        >
                          {highlightMatch(note.user)}
                        </MUITypography>
                        {assignedUser && (
                          <MUIStack
                            direction="row"
                            sx={{
                              alignItems: "center",
                            }}
                          >
                            <MUISubdirectoryArrowRightIcon
                              fontSize="small"
                              sx={{
                                color: (theme) => theme.palette.text.secondary,
                              }}
                            />
                            <MUITypography
                              variant="subtitle2"
                              sx={{
                                color: "text.secondary",
                              }}
                            >
                              {User.getDisplayName(assignedUser)}
                            </MUITypography>
                          </MUIStack>
                        )}
                      </MUITableCell>
                      <MUITableCell
                        style={{
                          verticalAlign: "top",
                          padding: "2px 8px 2px 6px",
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        {note.render}
                      </MUITableCell>
                      <MUITableCell
                        style={{
                          width: "48px",
                          verticalAlign: "top",
                          padding: "2px 4px",
                        }}
                      >
                        {canEdit ? (
                          editingNoteId === note.id ? (
                            <>
                              <MUIIconButton
                                size="small"
                                onClick={() => {
                                  setEditingNoteId(null);
                                  setEditingNoteText("");
                                }}
                              >
                                <MUICancelIcon fontSize="small" />
                              </MUIIconButton>
                              <MUIIconButton
                                size="small"
                                disabled={editingNoteText.trim() === ""}
                                onClick={handleUpdateNote}
                              >
                                <MUICheckIcon fontSize="small" />
                              </MUIIconButton>
                            </>
                          ) : (
                            <MUIIconButton
                              size="small"
                              onClick={() => {
                                setEditingNoteId(note.id);
                                setEditingNoteText(note.text);
                              }}
                            >
                              <MUIEditIcon fontSize="small" />
                            </MUIIconButton>
                          )
                        ) : null}
                        {/* Show icon for tasks assigned to current user and not completed */}
                        {canComplete && (
                          <MUIIconButton
                            size="small"
                            onClick={() => handleCompleteTask(note)}
                          >
                            <MUICheckBoxOutlineBlankIcon fontSize="small" />
                          </MUIIconButton>
                        )}
                      </MUITableCell>
                    </MUITableRow>
                  );
                })}
              </MUITableBody>
            </MUITable>
          </MUIBox>
        )}
      </>
    );
  };

  const noteTypeMenuOptions = [
    { key: "note", label: "Notes" },
    { key: "task", label: "Tasks" },
    { key: "auditLog.followupDate", label: "Followup Dates" },
    { key: "notification", label: "Notifications" },
    { key: "auditLog.orderStatusReasonId", label: "Routes" },
  ];
  const allFilterableEnabled = noteTypeMenuOptions.every(
    (noteTypeMenuOption) =>
      noteTypeVisibility[noteTypeMenuOption.key] === undefined ||
      noteTypeVisibility[noteTypeMenuOption.key],
  );

  // Helper to update visibility and persist setting
  const handleToggleNoteType = (type, event) => {
    if (event && event.ctrlKey) {
      // Ctrl+click: enable only the clicked type, disable all others
      const updated = Object.fromEntries(
        noteTypeMenuOptions.map((opt) => [opt.key, opt.key === type]),
      );
      Setting.set("chart.notes.visibility", updated);
      setNoteTypeVisibility(updated);
    } else {
      setNoteTypeVisibility((prev) => {
        const current = prev[type];
        const updated = {
          ...prev,
          [type]: current === undefined ? false : !current,
        };
        Setting.set("chart.notes.visibility", updated);
        return updated;
      });
    }
  };

  /**
   * Returns the action buttons for the card component. Includes create/cancel
   * note, export notes, and menu actions.
   * @returns {Array<JSX.Element>} Array of action button components.
   */
  const getActions = () => {
    const handleExportNotes = () => {
      const csvRows = [];

      //Remove render property from notes
      const filteredNotes = notes
        .map((note) => {
          const { render, ...rest } = note;
          return rest;
        })
        .filter((note) => note.type === "note");

      const keysToIgnore = [
        "id",
        "type",
        "createdById",
        "createdAt",
        "assignedToId",
        "completed",
      ];
      //Headers
      const headers = Object.keys(filteredNotes[0])
        .map((key) => {
          if (keysToIgnore.includes(key)) {
            return;
          }
          return key;
        })
        .filter((key) => key !== undefined);
      csvRows.push(headers);

      //Rows
      filteredNotes.forEach((note) => {
        const csvRow = [];
        headers.forEach((header) => {
          csvRow.push(note[header]);
        });
        csvRows.push(csvRow);
      });

      const sortedCsvRows = csvRows.slice(1).sort((a, b) => {
        return moment(b).diff(moment(a));
      });
      //Add headers back to sorted rows
      sortedCsvRows.unshift(csvRows[0]);

      const filename = `${chartContext.orderId ? `Order Notes` : `Patient Notes`} - ${chartContext.orderId ? chartContext.orderId : chartContext.patientId} - ${moment().format("YYYY-MM-DD h mm a")}.csv`;

      Exporter.exportCSV(sortedCsvRows, filename);
    };

    // Inline note type filter menu items
    const noteTypeMenuItems = noteTypeMenuOptions.map(({ key, label }) => (
      <MUIMenuItem key={key} onClick={(e) => handleToggleNoteType(key, e)}>
        <MUIListItemIcon>
          {noteTypeVisibility[key] === undefined || noteTypeVisibility[key] ? (
            <MUICheckBoxIcon fontSize="small" />
          ) : (
            <MUICheckBoxOutlineBlankIcon fontSize="small" />
          )}
        </MUIListItemIcon>
        {label}
      </MUIMenuItem>
    ));

    // Export menu item
    const exportMenuItem = (
      <MUIMenuItem
        onClick={() => handleExportNotes()}
        disabled={notes.length === 0}
      >
        <MUIListItemIcon>
          <MUIFileDownloadIcon fontSize="small" />
        </MUIListItemIcon>
        Export Notes
      </MUIMenuItem>
    );

    return [
      // Main actions
      <MUIBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {createNote && (
          <MUIButton
            size="small"
            color="error"
            onClick={() => {
              setCreateNote(false);
              setNoteText("");
              setNoteAssignedToId(mainContext.currentUser.id);
              setFollowUpDays(getDefaultFollowupDays());
              setNoteType(defaultNoteType);
              setSensitiveDataFindings([]);
              setSensitiveDataBypass(false);
            }}
          >
            Cancel
          </MUIButton>
        )}
        <MUIButton
          variant={createNote ? "contained" : "outlined"}
          startIcon={createNote ? <MUISaveIcon /> : <MUIAddIcon />}
          disabled={
            createNote &&
            (noteText.trim() === "" ||
              (sensitiveDataFindings.length > 0 && !sensitiveDataBypass))
          }
          size="small"
          onClick={async () => {
            if (createNote) {
              if (sensitiveDataFindings.length > 0 && !sensitiveDataBypass) {
                return;
              }
              const patient =
                chartContext.chart.patient[chartContext.patientId];
              if (noteType === followupNoteType) {
                chartContext.onChange({
                  orderActivity: {
                    [chartContext.orderId]: {
                      followUpDate: Calendar.addDays({
                        daysToAdd: followUpDays,
                        useBusinessDays: getDefaultUseBusinessDays(),
                      }),
                    },
                  },
                });
              }
              if (chartContext.orderId) {
                const newNoteId = chartContext.getNegativeId();
                const note = {
                  [newNoteId]: {
                    id: newNoteId,
                    accountId: patient.accountId,
                    activityId: chartContext.orderId,
                    assignedToId:
                      noteType === taskNoteType ? noteAssignedToId : null,
                    completed: noteType !== taskNoteType,
                    createdById: 1, // TODO Michael update note creation to not require this and throw it away if provided
                    createdAt: new Date().toISOString(),
                    dueDate: null,
                    isAutoVerification: false,
                    patientName: Patient.getDisplayName(patient),
                    task: noteType === taskNoteType,
                    text: noteText,
                    type: "ActivityNote",
                  },
                };
                chartContext.onChange({ note: note });
              } else {
                const newPatientNoteId = chartContext.getNegativeId();
                const patientNote = {
                  [newPatientNoteId]: {
                    id: newPatientNoteId,
                    note: noteText,
                    noteAddedBy: mainContext.currentUser.username,
                    noteAddedOn: new Date().toISOString(),
                    patientId: parseInt(chartContext.patientId),
                  },
                };
                chartContext.onChange({ patientNote: patientNote });
              }
              setPendingFollowUpSave(true);
            } else {
              setCreateNote(true);
            }
          }}
        >
          Create Note
        </MUIButton>
        {chartContext.orderId && (
          <Menu
            closeOnClick={false}
            icon={
              <MUIBadge
                color="secondary"
                variant="dot"
                overlap="circular"
                invisible={allFilterableEnabled}
              >
                <MUIFilterListIcon />
              </MUIBadge>
            }
            tooltip="Filter"
            menuItems={noteTypeMenuItems}
          />
        )}
        <Menu menuItems={[exportMenuItem]} />
      </MUIBox>,
    ];
  };

  /**
   * Returns the margin bottom value for the notes card. Used for "100%
   * height" mode.
   * @returns {number}
   */
  const getMarginBottom = () => {
    return 0;
  };

  return (
    <Card
      renderContent={renderContent}
      getActions={getActions}
      getTitle={() => title}
      getIcon={() => icon}

      getMarginBottom={getMarginBottom}
    />
  );
}

CardChartNotes.meta = { className: className, title: title, icon: icon };
